import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '../../../mocks/products';

const STORAGE_KEY = 'category_images';

function loadImages(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveImages(imgs: Record<string, string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(imgs));
}

export default function CategoriesSection() {
  const [editMode, setEditMode] = useState(false);
  const [catImages, setCatImages] = useState<Record<string, string>>(loadImages);
  const [savedMsg, setSavedMsg] = useState(false);
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getCatImage = (catId: string, fallback: string) =>
    catImages[catId] || fallback;

  const handleCatImageClick = (catId: string, e: React.MouseEvent) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    setEditingCatId(catId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingCatId) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const result = ev.target?.result as string;
      if (result) {
        const updated = { ...catImages, [editingCatId]: result };
        setCatImages(updated);
        saveImages(updated);
        setSavedMsg(true);
        setTimeout(() => setSavedMsg(false), 2000);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
    setEditingCatId(null);
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-black overflow-hidden relative" dir="rtl">
      {/* Top line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8721A]/30 to-transparent" />

      {/* Hidden File Input */}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* Saved Toast */}
      {savedMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] bg-[#E8721A] text-white text-sm font-bold px-6 py-3 flex items-center gap-2" style={{ clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-check-line" />
          </div>
          تم تغيير صورة الفئة بنجاح
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-14 flex-wrap gap-4">
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-16 h-px bg-gradient-to-l from-[#E8721A] to-transparent" />
              <span className="text-[#E8721A] text-xs font-bold tracking-[0.3em] uppercase">مجموعاتنا</span>
              <div className="w-16 h-px bg-gradient-to-r from-[#E8721A] to-transparent" />
            </div>
            <h2 className="text-4xl font-black text-white mb-3">تصفح حسب الفئة</h2>
            <p className="text-white/30 text-sm tracking-wide">اختر من أرقى المجموعات المختارة بعناية فائقة</p>
          </div>

          {/* Edit Toggle */}
          <button
            onClick={() => setEditMode(v => !v)}
            className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
              editMode
                ? 'bg-[#E8721A] text-white border-[#E8721A]'
                : 'bg-transparent text-white/50 border-white/10 hover:text-[#E8721A] hover:border-[#E8721A]/40'
            }`}
            style={{ clipPath: 'polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={editMode ? 'ri-check-double-line' : 'ri-image-edit-line'} />
            </div>
            {editMode ? 'إنهاء التعديل' : 'تعديل صور الفئات'}
          </button>
        </div>

        {/* Edit Mode Hint */}
        {editMode && (
          <div className="border border-[#E8721A]/20 bg-[#E8721A]/5 px-4 py-3 mb-8 flex items-center justify-center gap-3 text-xs text-[#E8721A]/70">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-camera-line" />
            </div>
            <span>اضغط على أي صورة فئة لتغييرها</span>
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {categories.map((cat, idx) => (
            <div key={cat.id} className="relative group">
              <Link
                to={editMode ? '#' : `/products?category=${cat.id}`}
                className="group relative overflow-hidden cursor-pointer block"
                onClick={e => editMode && e.preventDefault()}
              >
                {/* Image */}
                <div className="w-full h-64 lg:h-80 overflow-hidden relative">
                  <img
                    src={getCatImage(cat.id, cat.image)}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Edit overlay */}
                  {editMode && (
                    <button
                      onClick={e => handleCatImageClick(cat.id, e)}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 cursor-pointer z-10 transition-all hover:bg-black/75"
                    >
                      <div className="w-12 h-12 border-2 border-[#E8721A] flex items-center justify-center mb-2 hover:bg-[#E8721A] transition-colors">
                        <i className="ri-camera-line text-white text-xl" />
                      </div>
                      <span className="text-white text-xs font-bold tracking-widest uppercase">تغيير الصورة</span>
                    </button>
                  )}
                </div>

                {/* Cinematic overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10 transition-opacity duration-500" />
                <div className="absolute inset-0 bg-[#E8721A]/0 group-hover:bg-[#E8721A]/10 transition-all duration-500" />

                {/* Border accent on hover */}
                <div className="absolute inset-0 border border-transparent group-hover:border-[#E8721A]/40 transition-all duration-500" />

                {/* Content */}
                <div className="absolute bottom-0 right-0 left-0 p-5 z-20">
                  <div className="text-[#E8721A]/30 text-xs font-bold tracking-widest mb-2 uppercase">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-white text-lg font-black mb-1 group-hover:text-[#E8721A] transition-colors duration-300">{cat.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-white/30 text-xs tracking-widest">{cat.count} منتج</span>
                    <div className="w-7 h-7 flex items-center justify-center bg-[#E8721A]/0 group-hover:bg-[#E8721A] border border-white/20 group-hover:border-[#E8721A] transition-all duration-300">
                      <i className="ri-arrow-left-line text-white text-sm" />
                    </div>
                  </div>
                </div>

                {/* Top right label */}
                <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                  <span className="text-[10px] text-[#E8721A] font-bold tracking-widest uppercase bg-black/60 px-2 py-1">
                    {editMode ? 'تعديل' : 'تصفح'}
                  </span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
